// src/components/PrintDesigner/PrintLayoutRenderer.jsx
// يرسم تصميم الطباعة (v2) بالبيانات الفعلية — يُستخدم في المصمم والمعاينة والطباعة الحقيقية
//
//   <PrintLayoutRenderer layout={layout} data={invoiceData} />
//
// الإحداثيات بالملّيمتر من يسار المحتوى (اتجاه الصفحة ltr للتموضع فقط، والنصوص حسب dir كل عنصر)
import React, { forwardRef } from "react";
import { resolveText, totalsOf, money } from "./printVariables";
import { PAPER, contentWidth } from "./printTemplates";

const num = (v) => Number(v) || 0;
const px = (v) => `${v}px`;
const mm = (v) => `${v}mm`;

// لا نسمح بسكربتات داخل HTML المخصص
const safeHtml = (html) =>
  String(html || "")
    .replace(/<\s*script[\s\S]*?<\s*\/\s*script\s*>/gi, "")
    .replace(/\son\w+\s*=\s*(['"]).*?\1/gi, "");

const imgSrc = (v) => {
  if (!v) return null;
  if (/^(data:|https?:|\/)/.test(v)) return v;
  return `data:image/png;base64,${v}`; // Base64 بدون بادئة (مثل QR الزكاة)
};

// ───────────────────────── عنصر واحد ─────────────────────────
export function ElementView({ el, data, design = false }) {
  const p = el.props || {};
  const box = { width: "100%", height: "100%", boxSizing: "border-box", overflow: "hidden" };

  switch (el.type) {
    case "text": {
      const justify = { top: "flex-start", center: "center", bottom: "flex-end" }[p.vAlign || "center"];
      return (
        <div
          dir={p.dir || "rtl"}
          style={{
            ...box,
            display: "flex", flexDirection: "column", justifyContent: justify,
            fontSize: px(p.fontSize || 12), fontWeight: p.bold ? 800 : 500, fontStyle: p.italic ? "italic" : "normal",
            textDecoration: p.underline ? "underline" : "none", textAlign: p.align || "right",
            color: p.color || "#111827", background: p.bg || "transparent",
            border: p.borderWidth ? `${p.borderWidth}px ${p.borderStyle || "solid"} ${p.borderColor || "#111827"}` : "none",
            borderRadius: mm(p.radius || 0), padding: mm(p.padding ?? 1),
            lineHeight: p.lineHeight || 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word",
          }}
        >
          {resolveText(p.text, data)}
        </div>
      );
    }
    case "image":
    case "logo": {
      const src = el.type === "logo" || p.source === "companyLogo" ? imgSrc(data?.company?.logoUrl || data?.company?.logo || data?.company?.logoImage) : imgSrc(p.src);
      if (!src) {
        return design ? (
          <div style={{ ...box, display: "grid", placeItems: "center", border: "1px dashed #94a3b8", color: "#94a3b8", fontSize: 11, borderRadius: 4 }}>
            {el.type === "logo" ? "شعار المنشأة" : "صورة"}
          </div>
        ) : null;
      }
      return <img src={src} alt="" style={{ ...box, objectFit: p.fit || "contain", borderRadius: mm(p.radius || 0) }} />;
    }
    case "qr": {
      const src = imgSrc(data?.qrCodeUrl || data?.qrCode || data?.qrBase64);
      if (!src) {
        return design ? (
          <div style={{ ...box, display: "grid", placeItems: "center", border: "1px dashed #94a3b8", color: "#64748b", fontSize: 11, fontWeight: 700, background:
            "repeating-linear-gradient(45deg,#f1f5f9 0 4px,#fff 4px 8px)" }}>QR</div>
        ) : null;
      }
      return <img src={src} alt="QR" style={{ ...box, objectFit: "contain", imageRendering: "pixelated" }} />;
    }
    case "line": {
      const vertical = p.orientation === "vertical";
      const b = `${p.thickness || 1}px ${p.style || "solid"} ${p.color || "#111827"}`;
      return (
        <div style={{ ...box, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={vertical ? { height: "100%", borderLeft: b } : { width: "100%", borderTop: b }} />
        </div>
      );
    }
    case "rect":
      return (
        <div style={{
          ...box, background: p.bg || "transparent", borderRadius: mm(p.radius || 0),
          border: p.borderWidth ? `${p.borderWidth}px ${p.borderStyle || "solid"} ${p.borderColor || "#111827"}` : "none",
        }} />
      );
    case "totals":
      return <TotalsBlock props={p} data={data} />;
    case "html":
      return <div dir="rtl" style={box} dangerouslySetInnerHTML={{ __html: resolveText(safeHtml(p.html), data) }} />;
    default:
      return null;
  }
}

// ───────────────────────── الإجماليات ─────────────────────────
export function TotalsBlock({ props: p, data }) {
  const t = totalsOf(data);
  const rows = (p.rows || []).filter(r => r.visible);
  return (
    <div dir="rtl" style={{ width: "100%", height: "100%", fontSize: px(p.fontSize || 11), boxSizing: "border-box" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "inherit" }}>
        <tbody>
          {rows.map(r => {
            const isNet = r.key === "net";
            const cell = {
              padding: "1.2mm 2mm",
              border: p.border ? "1px solid #cbd5e1" : "none",
              borderBottom: p.border ? "1px solid #cbd5e1" : "1px solid #e5e7eb",
              ...(isNet && { background: p.netBg || "#111827", color: p.netColor || "#fff", fontWeight: 800 }),
            };
            return (
              <tr key={r.key}>
                <td style={{ ...cell, textAlign: "right", fontWeight: isNet ? 800 : 600 }}>
                  {r.labelAr}
                  {p.bilingual && r.labelEn && <span style={{ opacity: .7, fontWeight: 500, marginInlineStart: 6, fontSize: "0.85em" }}>{r.labelEn}</span>}
                </td>
                <td style={{ ...cell, textAlign: "left", direction: "ltr", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums", fontWeight: isNet ? 900 : 700 }}>
                  {money(t[r.key])}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ───────────────────────── جدول الأصناف ─────────────────────────
const cellValue = (key, it, i) => {
  const q = num(it.quantity), pr = num(it.unitPrice), d = num(it.discount), tx = num(it.taxValue);
  switch (key) {
    case "index": return i + 1;
    case "item": return it.arabicName || it.productName || it.name || it.englishName || "";
    case "unit": return it.unitName || "";
    case "quantity": return q;
    case "price": return money(pr);
    case "total": return money(q * pr);
    case "discount": return d ? money(d) : "—";
    case "tax": return money(tx);
    case "net": return money(it.net ?? it.lineTotal ?? q * pr - d + tx);
    case "description": return it.description || "";
    default: return "";
  }
};

export function ItemsTable({ table, data, sampleRows }) {
  const cols = (table.columns || []).filter(c => c.visible);
  const totalW = cols.reduce((s, c) => s + (Number(c.width) || 10), 0) || 1;
  const items = Array.isArray(data?.items) ? data.items : [];
  const rows = sampleRows ? items.slice(0, sampleRows) : items;
  const grid = table.border === "grid";
  const cellBorder = table.border === "none" ? "none" : "1px solid #cbd5e1";

  return (
    <table dir="rtl" style={{ width: "100%", borderCollapse: "collapse", fontSize: px(table.fontSize || 10), tableLayout: "fixed" }}>
      <colgroup>{cols.map(c => <col key={c.key} style={{ width: `${((Number(c.width) || 10) / totalW) * 100}%` }} />)}</colgroup>
      <thead style={{ display: "table-header-group" }}>
        <tr>
          {cols.map(c => (
            <th key={c.key} style={{
              background: table.headerBg || "#f1f5f9", color: table.headerColor || "#111827",
              padding: "1.4mm 1mm", fontWeight: 800, textAlign: "center", lineHeight: 1.3,
              border: grid ? cellBorder : "none", borderBottom: cellBorder,
              WebkitPrintColorAdjust: "exact", printColorAdjust: "exact",
            }}>
              {c.labelAr}
              {table.bilingual && c.labelEn && c.labelEn !== c.labelAr && (
                <div style={{ fontWeight: 600, fontSize: "0.82em", opacity: .75 }}>{c.labelEn}</div>
              )}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((it, i) => (
          <tr key={i} style={{ pageBreakInside: "avoid", background: table.zebra && i % 2 ? "#f8fafc" : "transparent", WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" }}>
            {cols.map(c => {
              const numeric = !["item", "unit", "description"].includes(c.key);
              return (
                <td key={c.key} style={{
                  padding: "1.2mm 1mm", textAlign: c.align || (numeric ? "center" : "right"),
                  border: grid ? cellBorder : "none", borderBottom: cellBorder, verticalAlign: "top",
                  direction: numeric ? "ltr" : "rtl", fontVariantNumeric: "tabular-nums", wordBreak: "break-word",
                  fontWeight: c.key === "net" ? 700 : 500,
                }}>
                  {cellValue(c.key, it, i)}
                  {c.key === "item" && table.showDescription && it.description && !cols.some(x => x.key === "description") && (
                    <div style={{ fontSize: "0.85em", color: "#64748b", marginTop: 1 }}>{it.description}</div>
                  )}
                </td>
              );
            })}
          </tr>
        ))}
        {!rows.length && (
          <tr><td colSpan={cols.length} style={{ padding: "3mm", textAlign: "center", color: "#94a3b8" }}>لا توجد أصناف</td></tr>
        )}
      </tbody>
    </table>
  );
}

// ───────────────────────── نطاق (رأس / ذيل) ─────────────────────────
export function Band({ layout, band, data }) {
  const els = layout.elements.filter(e => e.band === band).sort((a, b) => (a.z || 0) - (b.z || 0));
  return (
    <div style={{ position: "relative", height: mm(layout.bands[band]?.height || 0), width: "100%", pageBreakInside: "avoid" }}>
      {els.map(el => (
        <div key={el.id} style={{ position: "absolute", left: mm(el.x), top: mm(el.y), width: mm(el.w), height: mm(el.h), zIndex: el.z || 1 }}>
          <ElementView el={el} data={data} />
        </div>
      ))}
    </div>
  );
}

/** أنماط @page حسب المقاس — ضعها داخل المستند المطبوع */
export const pageCss = (layout) => {
  const paper = PAPER[layout.page.size] || PAPER.A4;
  const size = paper.thermal ? `${paper.w}mm auto` : `${paper.w}mm ${paper.h}mm`;
  return `
    @page { size: ${size}; margin: 0; }
    @media print { html, body { margin: 0; } .pl-page { box-shadow: none !important; } }
    .pl-page * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    ${layout.customCss || ""}
  `;
};

// ───────────────────────── الصفحة كاملة ─────────────────────────
const PrintLayoutRenderer = forwardRef(function PrintLayoutRenderer({ layout, data }, ref) {
  if (!layout) return null;
  const paper = PAPER[layout.page.size] || PAPER.A4;
  const margin = layout.page.margin ?? 8;
  return (
    <div
      ref={ref}
      className="pl-page"
      style={{
        width: mm(paper.w), minHeight: paper.h ? mm(paper.h) : undefined, boxSizing: "border-box",
        padding: mm(margin), background: "#fff", color: "#111827", direction: "ltr",
        fontSize: px(layout.page.fontSize || 11), fontFamily: "inherit",
      }}
    >
      <style>{pageCss(layout)}</style>
      <div style={{ width: mm(contentWidth(layout)) }}>
        <Band layout={layout} band="header" data={data} />
        {layout.table?.enabled !== false && (
          <div style={{ margin: "1.5mm 0" }}>
            <ItemsTable table={layout.table} data={data} />
          </div>
        )}
        <Band layout={layout} band="footer" data={data} />
      </div>
    </div>
  );
});

export default PrintLayoutRenderer;
